// src/util/privee_analytics.ts
//
// NOTE: Do NOT import `@/util/analytics` here. That would add a module-graph
// edge into the `/products/[slug]` chunk and trigger the Next.js 13 RSC
// chunk double-encoding bug (chunk URL becomes %255B...%255D and 404s).
// Instead, `SegmentInit` assigns `window.analytics = analytics` once at app
// init, and we read that here.

// ─── Shared property types ────────────────────────────────────────

export interface SkuProperties {
  parent_sku: string;
  parent_sku_tier: string;
}

export interface MembershipProperties {
  membership_tier: string;
  add_ons: boolean;
  insurance_coverage: string;
}

export interface ClosetProperties {
  closet_slots_filled: string;
  closet_slots_in_plan: string;
  parent_skus_in_closet: string;
  parent_sku_tiers_in_closet: string;
}

export interface ChildSkuProperties extends SkuProperties {
  child_sku: string;
}

// ─── Internal helper ──────────────────────────────────────────────

function track(event: string, properties: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const analytics = (
    window as unknown as {
      analytics?: { track?: (e: string, p: unknown) => void };
    }
  ).analytics;
  if (!analytics?.track) return;
  try {
    analytics.track(event, { ...properties });
  } catch {
    // Silently swallow — analytics must never break the UI
  }
}

// ─── PDP ──────────────────────────────────────────────────────────

export type PriveePdpUserState =
  | 'Available'
  | 'Too Early'
  | 'Too Early or Upgrade'
  | 'Upgrade or Return'
  | 'Return'
  | 'Resubscribe'
  | 'Reapproval'
  | 'Payment Needed'
  | 'Generic'
  | 'Item Unavailable';

export interface PdpUserStateProperties extends SkuProperties {
  add_to_closet_state: 'enabled' | 'disabled';
  cta_subtext?: string;
}

export function trackPriveePdpUserState(
  state: PriveePdpUserState,
  properties: PdpUserStateProperties
): void {
  track(`Réservé PDP User State - ${state}`, { ...properties });
}

export type PriveeAddToClosetVariant =
  | 'Clicked'
  | 'Too Early'
  | 'Too Early or Upgrade'
  | 'Upgrade or Return'
  | 'Return';

export interface AddToClosetClickedProperties extends SkuProperties {
  modal_text?: string;
}

export function trackPriveePdpAddToClosetClicked(
  variant: PriveeAddToClosetVariant,
  properties: AddToClosetClickedProperties
): void {
  // 'Clicked' uses a different event name shape than the modal variants
  const event =
    variant === 'Clicked'
      ? 'Réservé PDP Add to Closet - Clicked'
      : `Réservé PDP Add to Closet Clicked - ${variant}`;
  track(event, { ...properties });
}

export type PriveeModalCta =
  | 'Go to My Account'
  | 'Upgrade Your Membership'
  | 'Return an Item';

export function trackPriveePdpModalCtaClicked(
  cta: PriveeModalCta,
  properties: SkuProperties
): void {
  track(`Réservé PDP Modal CTA Clicked - ${cta}`, { ...properties });
}

export type PriveeAccordionSection = 'Description' | 'Size' | 'Condition';
export type PriveeAccordionAction = 'Expanded' | 'Collapsed';

export function trackPriveePdpAccordion(
  section: PriveeAccordionSection,
  action: PriveeAccordionAction,
  properties: SkuProperties
): void {
  track(`Réservé PDP ${section} - ${action}`, { ...properties });
}

// ─── Closet ───────────────────────────────────────────────────────

export function trackPriveeClosetViewed(
  properties: ClosetProperties & { return_button_visible: boolean }
): void {
  track('Réservé Closet - Viewed', { ...properties });
}

export interface EmptyClosetSlotProperties {
  closet_slots_filled: string;
  closet_slots_in_plan: string;
  destination: string;
}

export function trackPriveeEmptyClosetSlotClicked(
  properties: EmptyClosetSlotProperties
): void {
  track('Réservé Empty Closet Slot - Clicked', { ...properties });
}

export function trackPriveeClosetProductThumbnailClicked(
  properties: ClosetProperties
): void {
  track('Réservé Closet Product Thumbnail - Clicked', { ...properties });
}

export function trackPriveeClosetSlotProductDetailsClicked(
  properties: ClosetProperties
): void {
  track('Réservé Closet Slot Product Details - Clicked', { ...properties });
}

export function trackPriveeClosetReturnItemClicked(
  properties: ClosetProperties
): void {
  track('Réservé Closet Return Item - Clicked', { ...properties });
}

// ─── Manage Membership ────────────────────────────────────────────

export function trackPriveeEditMembershipTierClicked(
  properties: MembershipProperties
): void {
  track('Réservé Edit Membership Tier - Clicked', { ...properties });
}

export function trackPriveeFreezeMembershipClicked(
  properties: MembershipProperties
): void {
  track('Réservé Freeze Membership - Clicked', { ...properties });
}

export function trackPriveeCancelMembershipClicked(
  properties: MembershipProperties
): void {
  track('Réservé Cancel Membership - Clicked', { ...properties });
}

// ─── Unfreeze ─────────────────────────────────────────────────────

export function trackPriveeUnfreezeNavigationCtaClicked(
  properties: MembershipProperties
): void {
  track('Réservé Unfreeze Navigation CTA - Clicked', { ...properties });
}

export function trackPriveeUnfreezeMyMembershipClicked(
  properties: MembershipProperties
): void {
  track('Réservé Unfreeze My Membership - Clicked', { ...properties });
}

export function trackPriveeUnfreezeSubmitted(
  properties: MembershipProperties
): void {
  track('Réservé Unfreeze - Submitted', { ...properties });
}

export function trackPriveeUnfreezeCanceled(
  properties: MembershipProperties
): void {
  track('Réservé Unfreeze - Canceled', { ...properties });
}

// ─── Request Quote ────────────────────────────────────────────────

export function trackPriveeRequestQuoteClicked(
  properties: ClosetProperties
): void {
  track('Réservé Request to Purchase - Clicked', { ...properties });
}

export function trackPriveeRequestQuoteSelectItemClicked(
  properties: ChildSkuProperties
): void {
  track('Réservé Request to Purchase Select Item - Clicked', {
    ...properties
  });
}

export function trackPriveeRequestToPurchaseSubmitted(
  properties: ChildSkuProperties
): void {
  track('Réservé Request to Purchase - Submitted', { ...properties });
}

export function trackPriveeRequestQuoteCanceled(
  properties: ChildSkuProperties
): void {
  track('Réservé Request to Purchase - Canceled', { ...properties });
}

// ─── Application Lifecycle ────────────────────────────────────────

export function trackInterestedInPriveeClicked(
  properties: MembershipProperties
): void {
  track('Interested in Privée - Clicked', { ...properties });
}

// ─── Checkout ─────────────────────────────────────────────────────

export interface CheckoutOrderProperties extends SkuProperties {
  shipping_street_number: string;
  shipping_type: string;
  order_medium: 'Web' | 'App' | 'OP';
}

export function trackPriveeOrderPlacedCheckout(
  properties: CheckoutOrderProperties
): void {
  track('Order Placed - Checkout', { ...properties });
}

export function trackPriveeFirstOrderPlaced(
  properties: CheckoutOrderProperties
): void {
  track('Réservé Order - First Order Placed', { ...properties });
}

export function trackPriveeRemoveItemClicked(properties: SkuProperties): void {
  track('Remove Item - Clicked', { ...properties });
}

export function trackPriveeEditShippingClicked(
  properties: SkuProperties
): void {
  track('Edit Shipping - Clicked', { ...properties });
}

export interface ShippingAddressUpdatedProperties extends SkuProperties {
  new_address_street_number: string;
  previous_address_street_number: string;
}

export function trackPriveeShippingAddressUpdated(
  properties: ShippingAddressUpdatedProperties
): void {
  track('Shipping Address - Updated', { ...properties });
}

export function trackPriveeShippingMethodClicked(
  properties: SkuProperties
): void {
  track('Shipping Method - Clicked', { ...properties });
}

export function trackPriveeShippingMethodUpdated(
  properties: SkuProperties
): void {
  track('Shipping Method - Updated', { ...properties });
}

export function trackPriveeCheckoutFaqClicked(properties: SkuProperties): void {
  track('Checkout FAQ - Clicked', { ...properties });
}

export function trackPriveeOrderConfirmationFaqClicked(
  properties: SkuProperties
): void {
  track('Order Confirmation FAQ - Clicked', { ...properties });
}
